import React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { LuLoader, LuTrash } from "react-icons/lu";

// constants and icon
import { IoDuplicateOutline } from "react-icons/io5";
import { FaPlus } from "react-icons/fa6";

// router
import { useNavigate } from "react-router-dom";

// state and service
import {
  destroy_blog,
  get_blogs,
  update_status,
} from "../../../services/apis/blog";
import { useAppDispatch, useAppSelector } from "../../../store";
import Button from "../../../component/element/forms/Button";
import DataTable from "../../../component/element/table";
import ItemAction from "../../../component/ui/action/ItemAction";
import PopConfirm from "../../../component/ui/PopConfirm";
import { Switch } from "../../../component/ui/Switch";
import { IBlog } from "../../../store/types/blog";

const Blog = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { blogs, loading, limit, offset, total } = useAppSelector(
    ({ blog }) => blog,
  );
  const loadingBlogId = useAppSelector(({ blog }) => blog.loadingBlogId);
  const [updatingStatusBlogId, setUpdatingStatusBlogId] = React.useState<
    number | null
  >(null);

  React.useEffect(() => {
    get_blogs(dispatch);
  }, [dispatch]);

  const columns: ColumnDef<IBlog>[] = [
    {
      accessorKey: "title",
      header: "Title",
    },
    {
      accessorKey: "author_name",
      header: "Author",
    },
    {
      accessorKey: "is_published",
      header: "Status",
      cell: ({ row }) => {
        const blog = row.original;
        const isUpdating = updatingStatusBlogId === blog.id;
        return (
          <div className="flex items-center gap-2">
            <Switch
              checked={blog.is_published}
              disabled={updatingStatusBlogId !== null}
              aria-label={`${blog.is_published ? "Unpublish" : "Publish"} ${
                blog.title
              }`}
              onCheckedChange={async () => {
                setUpdatingStatusBlogId(blog.id);
                try {
                  await update_status(dispatch, String(blog.id));
                } finally {
                  setUpdatingStatusBlogId(null);
                }
              }}
            />
            {isUpdating && (
              <LuLoader
                size={16}
                className="animate-spin text-gray-500"
                aria-label="Updating status"
              />
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: "Created",
      cell: ({ getValue }) => (
        <span>{new Date(getValue<Date>()).toLocaleDateString()}</span>
      ),
    },
    {
      accessorKey: "id",
      header: "Action",
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <ItemAction
            show_delete={false}
            onView={() => navigate(`/blog/${row.original.id}`)}
            onEdit={() => navigate(`/blog/edit/${row.original.id}`)}
          />
          <PopConfirm
            title="Delete this blog?"
            description="This action cannot be undone."
            confirmText="Delete"
            onConfirm={() => destroy_blog(dispatch, row.original.id)}
          >
            <button
              type="button"
              disabled={loadingBlogId === row.original.id}
              aria-label={`Delete ${row.original.title}`}
              className="bg-red-50 hover:bg-red-300 text-red-800 p-2 rounded-md disabled:opacity-50"
            >
              {loadingBlogId === row.original.id ? (
                <LuLoader size={18} className="animate-spin" />
              ) : (
                <LuTrash size={18} />
              )}
            </button>
          </PopConfirm>
        </div>
      ),
    },
  ];

  const currentPage = Math.floor(offset / limit) + 1;
  const lastPage = Math.max(Math.ceil(total / limit), 1);

  return (
    <div className="w-full">
      <div className="w-full flex justify-between items-center mb-6">
        <h2 className="font-bold text-xl capitalize mb-0">Blogs</h2>

        <div className="flex items-center gap-x-2">
          <Button
            title="Create"
            children={<FaPlus className="mr-2" />}
            onClick={() => navigate("/blog/create")}
            className="px-4"
          />

          <Button
            title="Categories"
            children={<IoDuplicateOutline className="mr-2" />}
            onClick={() => navigate("/blog/categries")}
            className="text-white"
          />
        </div>
      </div>

      <div className="mt-4">
        {loading && blogs.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            Loading blogs...
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={blogs}
            current_page={currentPage}
            last_page={lastPage}
            onNext={(page) => get_blogs(dispatch, limit, (page - 1) * limit)}
            onPrev={(page) => get_blogs(dispatch, limit, (page - 1) * limit)}
          />
        )}
      </div>
    </div>
  );
};

export default Blog;
